/**
 * AddressSearch Component
 * 
 * Autocomplete search for addresses using Mapbox Geocoding API.
 * Returns structured address data for property creation/update.
 */

import React, { useState, useCallback, useRef, useMemo } from 'react';
import { View, Text, StyleSheet, Pressable, TextInput, ActivityIndicator, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useStyles as useStylesHook } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { authApi, AddressSearchResult } from '../../shared/utils/api';

interface AddressSearchProps {
  /** Called when an address is selected */
  onSelect: (result: AddressSearchResult) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Initial value */
  initialValue?: string;
  /** Bias results near these coordinates [lng, lat] */
  proximity?: [number, number];
  /** Error message to display */
  error?: string;
  /** Label for the input */
  label?: string;
}

export function AddressSearch({
  onSelect,
  placeholder = 'Search for an address...',
  initialValue = '',
  proximity,
  error,
  label,
}: AddressSearchProps) {
  const styleHook = useStylesHook();
  const { isMobile, isSmallMobile } = useResponsive();
  
  // Theme colors derived from styles
  const colors = useMemo(() => ({
    text: {
      primary: styleHook.tokens.color.onBackground,
      secondary: styleHook.tokens.color.onBackground + '80',
      tertiary: styleHook.tokens.color.onBackground + '60',
    },
    background: {
      primary: styleHook.tokens.color.background,
      secondary: styleHook.tokens.color.surface,
    },
    border: {
      primary: styleHook.tokens.color.border,
      secondary: styleHook.tokens.color.border + '60',
    },
    status: {
      error: styleHook.tokens.color.error,
    },
    primary: {
      main: styleHook.tokens.color.primary,
    },
  }), [styleHook]);
  
  const spacing = styleHook.utils.spacing;
  
  const [query, setQuery] = useState(initialValue);
  const [results, setResults] = useState<AddressSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<TextInput>(null);

  const searchAddresses = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      const response = await authApi.searchAddresses(searchQuery, {
        limit: 5,
        proximity,
      });

      if (response.success) {
        setResults(response.results);
        setShowResults(response.results.length > 0);
      } else {
        setSearchError(response.error || 'Search failed');
        setResults([]);
      }
    } catch (err) {
      setSearchError('Search failed');
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [proximity]);

  const handleQueryChange = useCallback((text: string) => {
    setQuery(text);
    
    // Debounce search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      searchAddresses(text);
    }, 300);
  }, [searchAddresses]);

  const handleSelect = useCallback((result: AddressSearchResult) => {
    setQuery(result.formatted_address);
    setShowResults(false);
    setResults([]);
    onSelect(result);
  }, [onSelect]);

  const handleClear = useCallback(() => {
    setQuery('');
    setResults([]);
    setShowResults(false);
    inputRef.current?.focus();
  }, []);

  const responsiveFontSize = isSmallMobile ? 14 : isMobile ? 15 : 16;

  return (
    <View style={componentStyles.container}>
      {label && (
        <Text style={[componentStyles.label, { color: colors.text.primary, marginBottom: spacing.xs }]}>
          {label}
        </Text>
      )}
      
      <View style={componentStyles.inputContainer}>
        <View style={componentStyles.inputWrapper}>
          <Ionicons
            name="search-outline"
            size={20}
            color={colors.text.tertiary}
            style={componentStyles.searchIcon}
          />
          <TextInput
            ref={inputRef}
            style={[
              componentStyles.input,
              {
                color: colors.text.primary,
                backgroundColor: colors.background.secondary,
                borderColor: error ? colors.status.error : colors.border.primary,
                fontSize: responsiveFontSize,
                paddingLeft: 40,
                paddingRight: query ? 40 : 16,
              },
            ]}
            value={query}
            onChangeText={handleQueryChange}
            placeholder={placeholder}
            placeholderTextColor={colors.text.tertiary}
            onFocus={() => {
              if (results.length > 0) {
                setShowResults(true);
              }
            }}
            onBlur={() => {
              // Delay hiding to allow click on results
              setTimeout(() => setShowResults(false), 200);
            }}
          />
          {isSearching && (
            <ActivityIndicator
              size="small"
              color={colors.primary.main}
              style={componentStyles.loadingIcon}
            />
          )}
          {!isSearching && query.length > 0 && (
            <Pressable onPress={handleClear} style={componentStyles.clearButton}>
              <Ionicons name="close-circle" size={20} color={colors.text.tertiary} />
            </Pressable>
          )}
        </View>

        {/* Results dropdown */}
        {showResults && results.length > 0 && (
          <View
            style={[
              componentStyles.resultsContainer,
              {
                backgroundColor: colors.background.primary,
                borderColor: colors.border.primary,
                shadowColor: colors.text.primary,
              },
            ]}
          >
            <ScrollView
              style={componentStyles.resultsList}
              keyboardShouldPersistTaps="handled"
              nestedScrollEnabled
            >
              {results.map((result, index) => (
                <Pressable
                  key={result.place_id || index}
                  style={({ pressed }) => [
                    componentStyles.resultItem,
                    {
                      backgroundColor: pressed
                        ? colors.background.secondary
                        : colors.background.primary,
                      borderBottomColor: colors.border.secondary,
                      borderBottomWidth: index < results.length - 1 ? 1 : 0,
                    },
                  ]}
                  onPress={() => handleSelect(result)}
                >
                  <Ionicons
                    name="location-outline"
                    size={18}
                    color={colors.primary.main}
                    style={componentStyles.resultIcon}
                  />
                  <View style={componentStyles.resultText}>
                    <Text
                      style={[componentStyles.resultTitle, { color: colors.text.primary }]}
                      numberOfLines={1}
                    >
                      {result.address?.street_address || result.formatted_address.split(',')[0]}
                    </Text>
                    <Text
                      style={[componentStyles.resultSubtitle, { color: colors.text.secondary }]}
                      numberOfLines={1}
                    >
                      {[result.address?.city, result.address?.state_abbr, result.address?.zip_code]
                        .filter(Boolean)
                        .join(', ')}
                    </Text>
                  </View>
                </Pressable>
              ))}
            </ScrollView>
          </View>
        )}
      </View>

      {(error || searchError) && (
        <Text style={[componentStyles.errorText, { color: colors.status.error, marginTop: spacing.xs }]}>
          {error || searchError}
        </Text>
      )}
    </View>
  );
}

const componentStyles = StyleSheet.create({
  container: {
    width: '100%',
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
  },
  inputContainer: {
    position: 'relative',
    zIndex: 10,
  },
  inputWrapper: {
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: [{ translateY: -10 }],
    zIndex: 1,
  },
  input: {
    height: 48,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
  },
  loadingIcon: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: [{ translateY: -10 }],
  },
  clearButton: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: [{ translateY: -10 }],
    padding: 4,
  },
  resultsContainer: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    borderWidth: 1,
    borderRadius: 8,
    marginTop: 4,
    maxHeight: 250,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  resultsList: {
    maxHeight: 250,
  },
  resultItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
  },
  resultIcon: {
    marginRight: 12,
  },
  resultText: {
    flex: 1,
  },
  resultTitle: {
    fontSize: 14,
    fontWeight: '500',
  },
  resultSubtitle: {
    fontSize: 12,
    marginTop: 2,
  },
  errorText: {
    fontSize: 12,
  },
});

export default AddressSearch;

